import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Shield, Check, X, MessageSquare, ArrowRight, ChevronDown, ChevronUp, Clock, AlertTriangle, Mic, Square, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { SourceBadge } from '@/components/SourceBadge';
import { formatLevel } from '@/lib/levels';
import LamnsoKeyboard from '@/components/LamnsoKeyboard';
import api from '@/lib/api';

export default function Validate() {
  const { user } = useAuth();
  const isElder = user?.level >= 4;
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const [focusedItem, setFocusedItem] = useState(null);
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewNote, setReviewNote] = useState({});
  const [suggestOpen, setSuggestOpen] = useState({});
  const [suggestLamnso, setSuggestLamnso] = useState({});
  const [suggestEnglish, setSuggestEnglish] = useState({});
  const [processing, setProcessing] = useState({});
  const [suggestKeyboard, setSuggestKeyboard] = useState({});
  const [expandedHistory, setExpandedHistory] = useState({});
  const [histories, setHistories] = useState({});
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [reviewed, setReviewedCount] = useState(0);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [totalConflicts, setTotalConflicts] = useState(0);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [totalSuggestions, setTotalSuggestions] = useState(0);
  const [totalSecondOpinion, setTotalSecondOpinion] = useState(0);

  useEffect(() => { setPage(0); }, [priorityFilter]);
  useEffect(() => { loadData(); }, [page, priorityFilter]); // eslint-disable-line
  useEffect(() => {
    if (focusId) {
      api.get(`/validations/focus/${focusId}`).then(res => {
        if (res.data.item) setFocusedItem(res.data.item);
      }).catch(err => console.error(err));
    }
  }, [focusId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const endpoint = isElder
        ? `/validations/elder-queue?limit=20&skip=${page * 20}&priority=${priorityFilter}`
        : `/validations/pending?limit=20`;
      const [queueRes, statsRes] = await Promise.all([
        api.get(endpoint),
        api.get('/validations/stats')
      ]);
      if (isElder) {
        setQueue(queueRes.data.candidates || []);
        setTotal(queueRes.data.total || 0);
        setReviewedCount(queueRes.data.reviewed_count || 0);
        setTotalConflicts(queueRes.data.total_conflicts || 0);
        setTotalCandidates(queueRes.data.total_candidates || 0);
        setTotalSuggestions(queueRes.data.total_suggestions || 0);
        setTotalSecondOpinion(queueRes.data.total_second_opinion || 0);
      } else {
        setQueue(queueRes.data.pending || []);
        setTotal(queueRes.data.total || 0);
      }
      setStats(statsRes.data);
    } catch (err) { console.error(err) }
    setLoading(false);
  };

  const handleElderReview = async (translationId, action) => {
    setProcessing(prev => ({ ...prev, [translationId]: true }));
    try {
      await api.post('/validations/elder-review', {
        translation_id: translationId,
        action: action,
        note: reviewNote[translationId] || '',
        suggested_lamnso: suggestLamnso[translationId] || '',
        suggested_english: suggestEnglish[translationId] || ''
      });
      setQueue(prev => prev.filter(t => t.translation_id !== translationId));
      setReviewNote(prev => ({ ...prev, [translationId]: '' }));
      setSuggestOpen(prev => ({ ...prev, [translationId]: false }));
      setSuggestLamnso(prev => ({ ...prev, [translationId]: '' }));
      setSuggestEnglish(prev => ({ ...prev, [translationId]: '' }));
      const statsRes = await api.get('/validations/stats');
      setStats(statsRes.data);
    } catch (err) { console.error(err) }
    setProcessing(prev => ({ ...prev, [translationId]: false }));
  };

  const handleSuggestionReview = async (suggestionId, action) => {
    setProcessing(prev => ({ ...prev, [suggestionId]: true }));
    try {
      await api.post(`/suggestions/${suggestionId}/review`, {
        action: action,
        note: reviewNote[suggestionId] || ''
      });
      setQueue(prev => prev.filter(t => t.translation_id !== suggestionId));
      setReviewNote(prev => ({ ...prev, [suggestionId]: '' }));
      loadData();
    } catch (err) { console.error(err) }
    setProcessing(prev => ({ ...prev, [suggestionId]: false }));
  };

  const toggleSuggest = (translationId) => {
    setSuggestOpen(prev => ({ ...prev, [translationId]: !prev[translationId] }));
  };

  const handleVote = async (translationId, vote) => {
    setProcessing(prev => ({ ...prev, [translationId]: true }));
    try {
      await api.post('/validations/vote', {
        translation_id: translationId,
        vote: vote
      });
      setQueue(prev => prev.filter(t => t.translation_id !== translationId));
      const statsRes = await api.get('/validations/stats');
      setStats(statsRes.data);
    } catch (err) { console.error(err) }
    setProcessing(prev => ({ ...prev, [translationId]: false }));
  };

  const toggleHistory = async (translationId) => {
    if (expandedHistory[translationId]) {
      setExpandedHistory(prev => ({ ...prev, [translationId]: false }));
      return;
    }
    try {
      const res = await api.get(`/validations/review-history/${translationId}`);
      setHistories(prev => ({ ...prev, [translationId]: res.data }));
      setExpandedHistory(prev => ({ ...prev, [translationId]: true }));
    } catch (err) { console.error(err) }
  };

  const confidenceColor = (score) => {
    if (score >= 0.9) return 'text-[#588157]';
    if (score >= 0.7) return 'text-[#D4A373]';
    return 'text-[#4CAF50]';
  };

  const confidenceBarColor = (score) => {
    if (score >= 0.9) return 'bg-[#588157]';
    if (score >= 0.7) return 'bg-[#D4A373]';
    return 'bg-[#4CAF50]';
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-safe">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="validate-title">
            {isElder ? 'Wirfon Review' : 'Validate Translations'}
          </h1>
          <p className="text-sm text-[#57534E] mt-1">
            {isElder ? 'Review and confirm or reject candidate translations' : 'Help validate translations by voting'}
          </p>
        </div>
        {isElder && (
          <span className="inline-flex items-center rounded-full bg-[#D4A373] px-3 py-1 text-xs font-bold text-white gap-1" data-testid="elder-badge">
            <Shield className="w-3 h-3" /> {formatLevel(4, 'combined')}
          </span>
        )}
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-6" data-testid="validation-stats">
          <StatPill label="Candidate" value={stats.candidate} color="text-[#D4A373]" />
          <StatPill label="Confirmed" value={stats.confirmed} color="text-[#588157]" />
          <StatPill label="Rejected" value={stats.rejected} color="text-[#4CAF50]" />
          <StatPill label="My Votes" value={stats.my_votes} color="text-[#57534E]" />
          {isElder && <StatPill label="My Reviews" value={stats.my_elder_reviews} color="text-[#D4A373]" />}
        </div>
      )}

      {/* Progress indicator for Elders */}
      {isElder && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 mb-4" data-testid="review-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[#1C1917]">Review Progress</span>
            <span className="text-xs text-[#A8A29E]">{reviewed} reviewed &middot; {total} in queue</span>
          </div>
          <div className="h-2 bg-[#F5F5F4] rounded-full overflow-hidden">
            <div className="h-full bg-[#D4A373] rounded-full transition-all" style={{ width: `${total + reviewed > 0 ? (reviewed / (total + reviewed)) * 100 : 0}%` }} />
          </div>
        </div>
      )}

      {/* Priority Filter Tabs (Elder only) */}
      {isElder && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1" data-testid="priority-filters">
          {[
            { key: 'all', label: 'All', count: null },
            { key: 'second_opinion', label: '2nd Opinion', count: totalSecondOpinion },
            { key: 'suggestions', label: 'Suggestions', count: totalSuggestions },
            { key: 'conflicts', label: 'Conflicts', count: totalConflicts },
            { key: 'voted', label: 'Community Voted', count: null },
            { key: 'candidates', label: 'Fresh', count: null },
          ].map(f => (
            <button key={f.key}
              onClick={() => setPriorityFilter(f.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                priorityFilter === f.key
                  ? 'bg-[#4CAF50] text-white'
                  : 'bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.key === 'conflicts' && <AlertTriangle className="w-3 h-3" />}
              {f.label}
              {f.count !== null && f.count > 0 && <span className="opacity-70">({f.count})</span>}
            </button>
          ))}
        </div>
      )}

      {/* Queue */}
      {/* Focused item from word detail */}
      {focusedItem && (
        <div className="mb-4" data-testid="focused-review-item">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-bold text-[#4CAF50] uppercase tracking-wider bg-[#E8F5E9] px-2 py-0.5 rounded-full">Reviewing</span>
            <button onClick={() => setFocusedItem(null)} className="text-[10px] text-[#A8A29E] hover:underline">Dismiss</button>
          </div>
          <div className="ring-2 ring-[#4CAF50] rounded-2xl">
      {[focusedItem].map((t) => (
            <div key={t.translation_id} className={`bg-white rounded-2xl border overflow-hidden ${
              t.queue_reason === 'meaning_conflict' ? 'border-[#EA580C]/40' : 'border-[#E7E5E4]'
            }`} data-testid={`focused-card-${t.translation_id}`}>
              <div className="p-5">
                {t.queue_reason && (
                  <div className="mb-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      t.queue_reason === 'meaning_conflict' ? 'bg-[#FFF7ED] text-[#EA580C]' : 'bg-[#E8F5E9] text-[#4CAF50]'
                    }`}>{t.queue_reason === 'meaning_conflict' ? 'Meaning Conflict' : 'Focused Review'}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <span className="text-xl font-semibold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {t.lamnso_word?.text || t.lamnso_text || '?'}
                  </span>
                  <ArrowRight className="w-5 h-5 text-[#A8A29E] flex-shrink-0" />
                  <span className="text-xl font-semibold text-[#4CAF50]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {t.english_word?.text || t.english_text || '?'}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-[#A8A29E] mb-3">
                  <SourceBadge source={t.source} />
                  <span className="font-semibold">{Math.round((t.confidence_score || 0) * 100)}% confidence</span>
                </div>
                {t.conflict && (
                  <div className="mb-4 p-3 rounded-xl bg-[#FFF7ED] border border-[#EA580C]/20">
                    <p className="text-[10px] font-bold text-[#EA580C] uppercase tracking-wider mb-1">Dictionary says:</p>
                    <p className="text-xs text-[#1C1917] leading-relaxed">{t.conflict.dictionary_sense?.slice(0, 200)}</p>
                  </div>
                )}
                {isElder && (
                  <div>
                    <div className="flex gap-2 mb-3">
                      <Button onClick={() => { handleElderReview(t.translation_id, 'confirm'); setFocusedItem(null); }}
                        className="flex-1 bg-[#588157] hover:bg-[#436442] text-white rounded-xl gap-1.5">
                        <Check className="w-4 h-4" /> Confirm
                      </Button>
                      <Button onClick={() => { handleElderReview(t.translation_id, 'reject'); setFocusedItem(null); }}
                        className="flex-1 bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl gap-1.5">
                        <X className="w-4 h-4" /> Reject
                      </Button>
                      <Button onClick={() => toggleSuggest(t.translation_id)}
                        variant="outline" className={`flex-1 rounded-xl gap-1.5 border-[#D4A373] text-[#D4A373] hover:bg-[#FDF6F0] ${suggestOpen[t.translation_id] ? 'bg-[#FDF6F0]' : ''}`}>
                        <MessageSquare className="w-4 h-4" /> Suggest
                      </Button>
                    </div>

                    {/* Suggest alternative form */}
                    {suggestOpen[t.translation_id] && (
                      <div className="mb-3 p-3 rounded-xl bg-[#FDF6F0] border border-[#D4A373]/30 space-y-2" data-testid={`suggest-form-${t.translation_id}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-[#D4A373]">Suggest an alternative</p>
                          <button onClick={() => setSuggestKeyboard(prev => ({...prev, [t.translation_id]: !prev[t.translation_id]}))}
                            className={`text-[9px] font-bold px-2 py-0.5 rounded-lg ${suggestKeyboard[t.translation_id] ? 'bg-[#4CAF50] text-white' : 'bg-[#E8F5E9] text-[#4CAF50]'}`}>
                            {suggestKeyboard[t.translation_id] ? 'ABC' : 'əŋ'}
                          </button>
                        </div>
                        <div className="relative">
                          {suggestKeyboard[t.translation_id] ? (
                            <div className="w-full rounded-xl border-0 py-2 px-3 text-xs ring-2 ring-inset ring-[#4CAF50] bg-white min-h-[36px] flex items-center"
                              style={{fontSize:'16px'}}>
                              <span className="text-xs">{suggestLamnso[t.translation_id] || ''}</span><span className="animate-pulse text-[#4CAF50]">|</span>
                            </div>
                          ) : (
                            <input
                              value={suggestLamnso[t.translation_id] || ''}
                              onChange={(e) => setSuggestLamnso(prev => ({ ...prev, [t.translation_id]: e.target.value }))}
                              placeholder={`Suggested Lamnso' (current: ${t.lamnso_word?.text || t.lamnso_text || ''})`}
                              className="w-full rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#D4A373]/40 focus:ring-[#D4A373] bg-white"
                              style={{fontSize:'16px'}}
                            />
                          )}
                        </div>
                        <input
                          value={suggestEnglish[t.translation_id] || ''}
                          onChange={(e) => setSuggestEnglish(prev => ({ ...prev, [t.translation_id]: e.target.value }))}
                          placeholder={`Suggested English (current: ${t.english_word?.text || t.english_text || ''})`}
                          className="w-full rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#D4A373]/40 focus:ring-[#D4A373] bg-white"
                          style={{fontSize:'16px'}}
                        />
                        {suggestKeyboard[t.translation_id] && (
                          <LamnsoKeyboard
                            onInsert={(char) => setSuggestLamnso(prev => ({...prev, [t.translation_id]: (prev[t.translation_id] || '') + char}))}
                            onDelete={() => setSuggestLamnso(prev => ({...prev, [t.translation_id]: (prev[t.translation_id] || '').slice(0, -1)}))}
                          />
                        )}
                        <div className="flex gap-2">
                          <Button onClick={() => { handleElderReview(t.translation_id, 'suggest_alternative'); setFocusedItem(null); }}
                            disabled={processing[t.translation_id] || (!suggestLamnso[t.translation_id] && !suggestEnglish[t.translation_id])}
                            className="bg-[#D4A373] hover:bg-[#B88A5E] text-white rounded-xl text-xs flex-1">
                            {processing[t.translation_id] ? '...' : 'Submit Suggestion'}
                          </Button>
                          <Button onClick={() => toggleSuggest(t.translation_id)}
                            variant="outline" className="rounded-xl text-xs">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    <input value={reviewNote[t.translation_id] || ''}
                      onChange={(e) => setReviewNote(prev => ({ ...prev, [t.translation_id]: e.target.value }))}
                      placeholder="Add a note (optional)..."
                      className="w-full rounded-xl border-0 py-2.5 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-[#FAFAF9]" />
                    {/* Pronunciation recording */}
                    <PronunciationRecorder entryId={t.translation_id} text={t.lamnso_word?.text || t.lamnso_text || ''} />
                  </div>
                )}
              </div>
            </div>
      ))}
          </div>
        </div>
      )}

      {queue.length > 0 ? (
        <div className="space-y-4" data-testid="validation-queue">
          {queue.map((t) => (
            <div key={t.translation_id} className={`bg-white rounded-2xl border overflow-hidden ${
              t.queue_reason === 'meaning_conflict' ? 'border-[#EA580C]/40' : 'border-[#E7E5E4]'
            }`} data-testid={`review-card-${t.translation_id}`}>
              {/* Translation pair */}
              <div className="p-5">
                {/* Priority badge */}
                {t.queue_reason && (
                  <div className="mb-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      t.queue_reason === 'meaning_conflict' ? 'bg-[#FFF7ED] text-[#EA580C]' :
                      t.queue_reason === 'needs_second_opinion' ? 'bg-[#E8F0FE] text-[#1967D2]' :
                      t.queue_reason === 'community_voted' ? 'bg-[#EAF1EA] text-[#588157]' :
                      t.queue_reason === 'community_suggestion' ? 'bg-[#FDF6F0] text-[#D4A373]' :
                      'bg-[#F5F5F4] text-[#A8A29E]'
                    }`} data-testid={`priority-badge-${t.translation_id}`}>
                      {t.queue_reason === 'meaning_conflict' ? 'Meaning Conflict' :
                       t.queue_reason === 'needs_second_opinion' ? '2nd Opinion Needed' :
                       t.queue_reason === 'community_voted' ? 'Community Voted' :
                       t.queue_reason === 'community_suggestion' ? 'Suggestion' :
                       'Fresh'}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <span className="text-xl font-semibold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {t.lamnso_word?.text || t.lamnso_text || '?'}
                  </span>
                  <ArrowRight className="w-5 h-5 text-[#A8A29E] flex-shrink-0" />
                  <span className="text-xl font-semibold text-[#4CAF50]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {t.english_word?.text || t.english_text || '?'}
                  </span>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-4 text-xs text-[#A8A29E] mb-3">
                  <SourceBadge source={t.source} />
                  <span className={`font-semibold ${confidenceColor(t.confidence_score)}`}>
                    {Math.round(t.confidence_score * 100)}% confidence
                  </span>
                  {t.vote_count !== undefined && (
                    <span>{t.vote_count} vote{t.vote_count !== 1 ? 's' : ''} ({t.correct_votes} correct)</span>
                  )}
                  {t.lamnso_word?.part_of_speech && (
                    <span className="bg-[#F5F5F4] px-2 py-0.5 rounded">{t.lamnso_word.part_of_speech}</span>
                  )}
                </div>

                {/* Confidence bar */}
                <div className="h-1.5 bg-[#F5F5F4] rounded-full overflow-hidden mb-4">
                  <div className={`h-full rounded-full ${confidenceBarColor(t.confidence_score)}`}
                    style={{ width: `${Math.round(t.confidence_score * 100)}%` }} />
                </div>

                {/* Conflict detail (for meaning_conflict items) */}
                {t.conflict && (
                  <div className="mb-4 p-3 rounded-xl bg-[#FFF7ED] border border-[#EA580C]/20" data-testid={`conflict-detail-${t.translation_id}`}>
                    <p className="text-[10px] font-bold text-[#EA580C] uppercase tracking-wider mb-1">Dictionary says:</p>
                    <p className="text-xs text-[#1C1917] leading-relaxed">{t.conflict.dictionary_sense?.slice(0, 200)}</p>
                  </div>
                )}

                {/* Suggestion detail */}
                {t.suggestion && (
                  <div className="mb-4 p-3 rounded-xl bg-[#ECFEFF] border border-[#0891B2]/20" data-testid={`suggestion-detail-${t.translation_id}`}>
                    <p className="text-[10px] font-bold text-[#0891B2] uppercase tracking-wider mb-1">Community Suggestion</p>
                    <p className="text-xs text-[#1C1917]">
                      By <strong>{t.suggestion.submitter_name}</strong> ({formatLevel(t.suggestion.submitter_level, 'short')})
                    </p>
                    {t.suggestion.note && <p className="text-[10px] text-[#57534E] mt-1 italic">"{t.suggestion.note}"</p>}
                    {t.suggestion.example_context && <p className="text-[10px] text-[#57534E] mt-0.5">Example: {t.suggestion.example_context}</p>}
                    <p className="text-[10px] text-[#A8A29E] mt-1">Votes: {t.suggestion.vote_score} (weighted: {t.suggestion.weighted_vote_score?.toFixed(1)})</p>
                  </div>
                )}

                {/* Elder controls — different for suggestions vs translations */}
                {isElder && t.queue_reason === 'community_suggestion' ? (
                  <div>
                    <div className="flex gap-2 mb-3">
                      <Button onClick={() => handleSuggestionReview(t.suggestion.suggestion_id, 'approve')}
                        disabled={processing[t.translation_id]}
                        className="flex-1 bg-[#588157] hover:bg-[#436442] text-white rounded-xl gap-1.5"
                        data-testid={`approve-sug-${t.translation_id}`}>
                        <Check className="w-4 h-4" /> Approve & Add
                      </Button>
                      <Button onClick={() => handleSuggestionReview(t.suggestion.suggestion_id, 'reject')}
                        disabled={processing[t.translation_id]}
                        className="flex-1 bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl gap-1.5"
                        data-testid={`reject-sug-${t.translation_id}`}>
                        <X className="w-4 h-4" /> Reject
                      </Button>
                    </div>
                    <input value={reviewNote[t.translation_id] || ''}
                      onChange={(e) => setReviewNote(prev => ({ ...prev, [t.translation_id]: e.target.value }))}
                      placeholder="Review note (optional)..."
                      className="w-full rounded-xl border-0 py-2.5 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-[#FAFAF9]"
                      data-testid={`review-note-${t.translation_id}`} />
                  </div>
                ) : isElder ? (
                  <div>
                    <div className="flex gap-2 mb-3">
                      <Button onClick={() => handleElderReview(t.translation_id, 'confirm')}
                        disabled={processing[t.translation_id]}
                        className="flex-1 bg-[#588157] hover:bg-[#436442] text-white rounded-xl gap-1.5"
                        data-testid={`elder-confirm-${t.translation_id}`}
                      >
                        <Check className="w-4 h-4" /> Confirm
                      </Button>
                      <Button onClick={() => handleElderReview(t.translation_id, 'reject')}
                        disabled={processing[t.translation_id]}
                        className="flex-1 bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl gap-1.5"
                        data-testid={`elder-reject-${t.translation_id}`}
                      >
                        <X className="w-4 h-4" /> Reject
                      </Button>
                      <Button onClick={() => toggleSuggest(t.translation_id)}
                        variant="outline" className={`flex-1 rounded-xl gap-1.5 border-[#D4A373] text-[#D4A373] hover:bg-[#FDF6F0] ${suggestOpen[t.translation_id] ? 'bg-[#FDF6F0]' : ''}`}
                        data-testid={`elder-suggest-${t.translation_id}`}
                      >
                        <MessageSquare className="w-4 h-4" /> Suggest
                      </Button>
                    </div>

                    {/* Suggest alternative form */}
                    {suggestOpen[t.translation_id] && (
                      <div className="mb-3 p-3 rounded-xl bg-[#FDF6F0] border border-[#D4A373]/30 space-y-2" data-testid={`suggest-form-${t.translation_id}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-[#D4A373]">Suggest an alternative</p>
                          <button onClick={() => setSuggestKeyboard(prev => ({...prev, [t.translation_id]: !prev[t.translation_id]}))}
                            className={`text-[9px] font-bold px-2 py-0.5 rounded-lg ${suggestKeyboard[t.translation_id] ? 'bg-[#4CAF50] text-white' : 'bg-[#E8F5E9] text-[#4CAF50]'}`}>
                            {suggestKeyboard[t.translation_id] ? 'ABC' : 'əŋ'}
                          </button>
                        </div>
                        <div className="relative">
                          {suggestKeyboard[t.translation_id] ? (
                            <div className="w-full rounded-xl border-0 py-2 px-3 text-xs ring-2 ring-inset ring-[#4CAF50] bg-white min-h-[36px] flex items-center"
                              style={{fontSize:'16px'}}>
                              <span className="text-xs">{suggestLamnso[t.translation_id] || ''}</span><span className="animate-pulse text-[#4CAF50]">|</span>
                            </div>
                          ) : (
                            <input
                              value={suggestLamnso[t.translation_id] || ''}
                              onChange={(e) => setSuggestLamnso(prev => ({ ...prev, [t.translation_id]: e.target.value }))}
                              placeholder={`Suggested Lamnso' (current: ${t.lamnso_word?.text || t.lamnso_text || ''})`}
                              className="w-full rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#D4A373]/40 focus:ring-[#D4A373] bg-white"
                              style={{fontSize:'16px'}}
                              data-testid={`suggest-lamnso-${t.translation_id}`}
                            />
                          )}
                        </div>
                        <input
                          value={suggestEnglish[t.translation_id] || ''}
                          onChange={(e) => setSuggestEnglish(prev => ({ ...prev, [t.translation_id]: e.target.value }))}
                          placeholder={`Suggested English (current: ${t.english_word?.text || t.english_text || ''})`}
                          className="w-full rounded-xl border-0 py-2 px-3 text-xs ring-1 ring-inset ring-[#D4A373]/40 focus:ring-[#D4A373] bg-white"
                          style={{fontSize:'16px'}}
                          data-testid={`suggest-english-${t.translation_id}`}
                        />
                        {suggestKeyboard[t.translation_id] && (
                          <LamnsoKeyboard
                            onInsert={(char) => setSuggestLamnso(prev => ({...prev, [t.translation_id]: (prev[t.translation_id] || '') + char}))}
                            onDelete={() => setSuggestLamnso(prev => ({...prev, [t.translation_id]: (prev[t.translation_id] || '').slice(0, -1)}))}
                          />
                        )}
                        <div className="flex gap-2">
                          <Button onClick={() => handleElderReview(t.translation_id, 'suggest_alternative')}
                            disabled={processing[t.translation_id] || (!suggestLamnso[t.translation_id] && !suggestEnglish[t.translation_id])}
                            className="bg-[#D4A373] hover:bg-[#B88A5E] text-white rounded-xl text-xs flex-1"
                            data-testid={`submit-suggest-${t.translation_id}`}
                          >
                            {processing[t.translation_id] ? '...' : 'Submit Suggestion'}
                          </Button>
                          <Button onClick={() => toggleSuggest(t.translation_id)}
                            variant="outline" className="rounded-xl text-xs"
                            data-testid={`cancel-suggest-${t.translation_id}`}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    <input
                      value={reviewNote[t.translation_id] || ''}
                      onChange={(e) => setReviewNote(prev => ({ ...prev, [t.translation_id]: e.target.value }))}
                      placeholder="Add a note (optional)..."
                      className="w-full rounded-xl border-0 py-2.5 px-3 text-xs ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-[#FAFAF9]"
                      data-testid={`review-note-${t.translation_id}`}
                    />
                    {/* Pronunciation recording */}
                    <PronunciationRecorder entryId={t.translation_id} text={t.lamnso_word?.text || t.lamnso_text || ''} />
                  </div>
                ) : (
                  /* Community voting controls */
                  <div className="flex gap-2">
                    <Button onClick={() => handleVote(t.translation_id, 'correct')}
                      disabled={processing[t.translation_id]}
                      className="flex-1 bg-[#588157] hover:bg-[#436442] text-white rounded-xl gap-1.5"
                      data-testid={`vote-correct-${t.translation_id}`}
                    >
                      <Check className="w-4 h-4" /> Correct
                    </Button>
                    <Button onClick={() => handleVote(t.translation_id, 'incorrect')}
                      disabled={processing[t.translation_id]}
                      className="flex-1 bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl gap-1.5"
                      data-testid={`vote-incorrect-${t.translation_id}`}
                    >
                      <X className="w-4 h-4" /> Incorrect
                    </Button>
                  </div>
                )}
              </div>

              {/* History toggle */}
              <button onClick={() => toggleHistory(t.translation_id)}
                className="w-full flex items-center justify-center gap-1 py-2.5 border-t border-[#E7E5E4] text-xs text-[#A8A29E] hover:bg-[#FAFAF9] transition-colors"
                data-testid={`toggle-history-${t.translation_id}`}
              >
                <Clock className="w-3 h-3" />
                Review History
                {expandedHistory[t.translation_id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>

              {expandedHistory[t.translation_id] && histories[t.translation_id] && (
                <div className="border-t border-[#E7E5E4] p-4 bg-[#FAFAF9] space-y-2" data-testid={`history-${t.translation_id}`}>
                  {histories[t.translation_id].elder_reviews?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-[#D4A373] uppercase tracking-wider mb-1">Wirfon Reviews</p>
                      {histories[t.translation_id].elder_reviews.map(r => (
                        <div key={r.review_id} className="flex items-start gap-2 py-1.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            r.action === 'confirm' ? 'bg-[#EAF1EA] text-[#588157]' :
                            r.action === 'reject' ? 'bg-[#E8F5E9] text-[#4CAF50]' :
                            'bg-[#FDF6F0] text-[#D4A373]'
                          }`}>{r.action}</span>
                          <div className="flex-1">
                            <span className="text-[10px] text-[#57534E]">{r.reviewer_name}</span>
                            {r.note && <p className="text-[10px] text-[#A8A29E] mt-0.5">"{r.note}"</p>}
                          </div>
                          <span className="text-[10px] text-[#A8A29E]">{formatTime(r.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {histories[t.translation_id].community_votes?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-[#57534E] uppercase tracking-wider mb-1">Community Votes</p>
                      {histories[t.translation_id].community_votes.map(v => (
                        <div key={v.validation_id} className="flex items-center gap-2 py-1">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            v.vote === 'correct' ? 'bg-[#EAF1EA] text-[#588157]' : 'bg-[#E8F5E9] text-[#4CAF50]'
                          }`}>{v.vote}</span>
                          <span className="text-[10px] text-[#57534E]">{v.user_name} (L{v.user_level})</span>
                          <span className="text-[10px] text-[#A8A29E] ml-auto">{formatTime(v.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(!histories[t.translation_id].elder_reviews?.length && !histories[t.translation_id].community_votes?.length) && (
                    <p className="text-[10px] text-[#A8A29E] text-center py-2">No review history yet</p>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Pagination for Elders */}
          {isElder && total > 20 && (
            <div className="flex items-center justify-center gap-3 pt-4" data-testid="pagination">
              <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0} className="rounded-xl text-xs">
                Previous
              </Button>
              <span className="text-xs text-[#57534E]">Page {page + 1} of {Math.ceil(total / 20)}</span>
              <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * 20 >= total} className="rounded-xl text-xs">
                Next
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-16" data-testid="empty-queue">
          <Check className="w-12 h-12 text-[#588157] mx-auto mb-3" />
          <p className="text-[#1C1917] font-semibold">All caught up!</p>
          <p className="text-sm text-[#A8A29E] mt-1">
            {isElder ? 'No more candidate translations to review.' : 'No more translations to vote on.'}
          </p>
        </div>
      )}
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-[#E7E5E4] px-3 py-2 text-center">
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      <p className="text-[10px] text-[#A8A29E]">{label}</p>
    </div>
  );
}


function PronunciationRecorder({ entryId, text }) {
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const { recording, audioBlob, audioUrl, error, startRecording, stopRecording, reset: resetRecorder, isSupported } = require('@/hooks/useAudioRecorder').useAudioRecorder();

  const uploadRecording = async () => {
    if (!audioBlob) return;
    setUploading(true);
    try {
      const formData = new FormData();
      const ext = audioBlob.type?.includes('mp4') ? 'mp4' : audioBlob.type?.includes('ogg') ? 'ogg' : 'webm';
      formData.append('file', audioBlob, `pronunciation.${ext}`);
      await api.post(`/audio/pronunciation?entry_id=${encodeURIComponent(entryId)}&text=${encodeURIComponent(text)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploaded(true);
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const handleReset = () => {
    resetRecorder();
    setUploaded(false);
  };

  if (!isSupported) return null;

  if (uploaded) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-[#588157]" data-testid="pronunciation-uploaded">
        <Check className="w-3.5 h-3.5" /> Pronunciation recorded! <button onClick={handleReset} className="text-[#A8A29E] underline">Record another</button>
      </div>
    );
  }

  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="pronunciation-recorder">
      {error && <p className="text-[10px] text-[#4CAF50] bg-[#E8F5E9] px-2 py-1 rounded-lg w-full">{error}</p>}
      {!audioBlob ? (
        <button onClick={recording ? stopRecording : startRecording}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
            recording ? 'bg-[#4CAF50] text-white animate-pulse' : 'bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]'
          }`}
          data-testid="record-btn">
          {recording ? <><Square className="w-3 h-3" /> Stop</> : <><Mic className="w-3 h-3" /> Record pronunciation</>}
        </button>
      ) : (
        <>
          <audio src={audioUrl} controls className="h-8" style={{ maxWidth: '160px' }} />
          <Button size="sm" onClick={uploadRecording} disabled={uploading}
            className="bg-[#588157] hover:bg-[#436442] text-white rounded-xl text-xs gap-1" data-testid="upload-pronunciation-btn">
            {uploading ? '...' : 'Save'}
          </Button>
          <button onClick={handleReset} className="text-[10px] text-[#A8A29E] hover:underline">Discard</button>
        </>
      )}
      <span className="text-[9px] text-[#A8A29E]">Optional</span>
    </div>
  );
}
